import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { http, money } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { TID } from "@/lib/testIds";
import { Minus, Plus, Check, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState({ currency_symbol: "₹" });
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const { add } = useCart();

  useEffect(() => {
    http.get(`/store/products/${slug}`).then((r) => setProduct(r.data)).catch(() => nav("/products"));
    http.get("/store/info").then((r) => setStore(r.data));
  }, [slug, nav]);

  if (!product) return <div className="bls-container py-24"><div className="bls-shimmer h-96 rounded-2xl" /></div>;

  const images = product.images?.length ? product.images : [];

  return (
    <div className="bls-container py-12">
      <Link to="/products" className="text-sm text-black/50 hover:text-[#534ab7]">← Back to all</Link>
      <div className="mt-6 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="bls-thumb rounded-2xl mb-4 max-h-[520px]">
            {images[imgIdx] && <img src={images[imgIdx]} alt={product.name} className="w-full h-full object-cover" />}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {images.map((src, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${i === imgIdx ? "border-[#7f77dd]" : "border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          {product.badge && <span className="bls-tag bls-tag-green">{product.badge}</span>}
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter">{product.name}</h1>
          <div className="mt-4 flex items-end gap-3">
            <span className="font-display text-3xl font-bold">{money(product.price, store.currency_symbol)}</span>
            {product.compare_at_price && <span className="bls-old-price text-base">{money(product.compare_at_price, store.currency_symbol)}</span>}
            {product.compare_at_price && (
              <span className="bls-tag bls-tag-green">
                Save {Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)}%
              </span>
            )}
          </div>
          <p className="mt-6 text-black/70 leading-relaxed">{product.short_description}</p>
          <div
            className="mt-4 prose prose-sm max-w-none text-black/70 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: product.description || "" }}
          />

          <div className="mt-8 flex items-center gap-4">
            <div className="inline-flex items-center border border-black/15 rounded-full">
              <button className="w-9 h-9 inline-flex items-center justify-center" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="pdp-qty-dec">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-9 text-center font-medium">{qty}</span>
              <button className="w-9 h-9 inline-flex items-center justify-center" onClick={() => setQty((q) => q + 1)} data-testid="pdp-qty-inc">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => { add(product, qty); toast.success(`Added ${qty} × ${product.name}`); }}
              className="bls-btn-primary flex-1 sm:flex-none"
              data-testid={`${TID.productAddBtn}-detail`}
            >
              Add to cart
            </button>
            <button
              onClick={() => { add(product, qty); nav("/checkout"); }}
              className="bls-btn-secondary"
              data-testid={`${TID.productBuyBtn}`}
            >
              Buy now
            </button>
          </div>

          <ul className="mt-8 space-y-3 text-sm text-black/70">
            <li className="flex items-center gap-3"><Truck className="w-4 h-4 text-[#534ab7]" /> Free shipping across India</li>
            <li className="flex items-center gap-3"><RotateCcw className="w-4 h-4 text-[#534ab7]" /> 7-day easy returns</li>
            <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-[#534ab7]" /> COD available at checkout</li>
            <li className="flex items-center gap-3"><Check className="w-4 h-4 text-[#534ab7]" /> {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
